import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';
import { Form, Select } from 'antd';
import { useGetManyClients } from 'api/clients/use-get-many-clients.ts';
import { ClientType } from 'types/client.ts';

interface IClientMultiSelectFilterProps {
  form: FormInstance;
  name: string;
  onSetValue: () => void;
  formInputClassName?: string;
}

export const CarrierMultiSelectFilter: FC<IClientMultiSelectFilterProps> = ({
  name,
  formInputClassName,
  form,
  onSetValue,
}) => {
  const { t } = useTranslation();
  const [clientSearchValue, setClientSearchValue] = useState<string>('');
  const [items, setItems] = useState<{ value: number | string; label: string }[]>([]);
  const { data: clients } = useGetManyClients({
    limit: 10,
    search: clientSearchValue,
    clientTypes: [ClientType.carrier, ClientType.expedition],
  });

  const handleSearch = (val: string) => setClientSearchValue(val);

  useEffect(() => {
    const clientsData = (clients?.data || []).map((client) => ({
      value: client.id,
      label: client.shortName || client.name,
    }));

    if (clientSearchValue) {
      setItems(clientsData);
    } else {
      setItems([
        { value: 'none', label: t('transport:without_subcontractor') },
        {
          value: 'any',
          label: t('transport:with_subcontractor'),
        },
        ...clientsData,
      ]);
    }
  }, [clients?.data, clientSearchValue]);

  const handleSuccess = (value: 'none' | 'any' | number[]) => {
    if (value[value.length - 1] === 'none') {
      form.setFieldsValue({ [name]: 'none' });
    } else if (value[value.length - 1] === 'any') {
      form.setFieldsValue({ [name]: 'any' });
    } else if (Array.isArray(value)) {
      form.setFieldsValue({ [name]: value.filter((t) => !isNaN(Number(t))) });
    }

    setClientSearchValue('');
    onSetValue();
  };

  const getValue = (value: unknown) => {
    let valueForSelect = undefined;

    if (value) {
      valueForSelect = Array.isArray(value) ? value : [value];
    }
    return { value: valueForSelect };
  };

  return (
    <Form.Item name={name} className={formInputClassName} getValueProps={getValue}>
      <Select
        placeholder={t('transport:subcontractor')}
        mode='multiple'
        optionFilterProp='label'
        options={items}
        allowClear
        showSearch
        onChange={handleSuccess}
        onSearch={handleSearch}
      />
    </Form.Item>
  );
};
